import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Upload, X, ArrowLeft, LogOut, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { getUserFriendlyError } from "@/lib/errors";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  active: boolean;
  show_on_homepage: boolean;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
}

const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    active: true,
    show_on_homepage: false,
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdmin();
    fetchProducts();
  }, []);

  const checkAdmin = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (error || !data) {
      toast({
        title: "Geen toegang",
        description: "Je hebt geen admin rechten",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    setIsAdmin(true);
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error: unknown) {
      toast({
        title: "Fout bij laden producten",
        description: getUserFriendlyError(error, "Producten laden"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      if (isEdit && selectedProduct) {
        setSelectedProduct({ ...selectedProduct, image_url: publicUrl });
      } else {
        setNewProduct({ ...newProduct, image_url: publicUrl });
      }

      toast({ title: "Afbeelding geüpload!" });
    } catch (error: unknown) {
      toast({
        title: "Upload fout",
        description: getUserFriendlyError(error, "Afbeelding uploaden"),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const createProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // First create the Stripe product and price
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke(
        "create-stripe-product",
        {
          body: {
            name: newProduct.name,
            description: newProduct.description,
            price: parseFloat(newProduct.price),
            imageUrl: newProduct.image_url,
          },
        }
      );

      if (stripeError) throw stripeError;

      // Then create the product in the database with the Stripe IDs
      const { error } = await supabase.from("products").insert({
        name: newProduct.name,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        image_url: newProduct.image_url,
        active: newProduct.active,
        show_on_homepage: newProduct.show_on_homepage,
        stripe_price_id: stripeData.priceId,
        stripe_product_id: stripeData.productId,
      });

      if (error) throw error;

      toast({ title: "Product toegevoegd en gesynchroniseerd met Stripe!" });
      setNewProduct({ name: "", description: "", price: "", image_url: "", active: true, show_on_homepage: false });
      setShowNewProductForm(false);
      fetchProducts();
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Product toevoegen"),
        variant: "destructive",
      });
    }
  };

  const updateProduct = async () => {
    if (!selectedProduct) return;

    try {
      // Update the database first
      const { error } = await supabase
        .from("products")
        .update({
          name: selectedProduct.name,
          description: selectedProduct.description,
          price: selectedProduct.price,
          image_url: selectedProduct.image_url,
          active: selectedProduct.active,
          show_on_homepage: selectedProduct.show_on_homepage,
        })
        .eq("id", selectedProduct.id);

      if (error) throw error;

      // Sync product info to Stripe (WITHOUT creating a new price)
      if (selectedProduct.stripe_product_id) {
        const { error: stripeError } = await supabase.functions.invoke(
          "create-stripe-product",
          {
            body: {
              name: selectedProduct.name,
              description: selectedProduct.description,
              price: selectedProduct.price,
              imageUrl: selectedProduct.image_url,
              stripeProductId: selectedProduct.stripe_product_id,
              syncPrice: false, // Don't create a new price
            },
          }
        );

        if (stripeError) {
          console.error("Stripe sync error:", stripeError);
          // Don't throw, just log - local update was successful
        }
      }

      toast({ title: "Product bijgewerkt!" });
      setSelectedProduct(null);
      fetchProducts();
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Product bijwerken"),
        variant: "destructive",
      });
    }
  };

  const syncProductWithStripe = async () => {
    if (!selectedProduct) return;

    try {
      toast({ title: "Product synchroniseren met Stripe..." });

      const { data: stripeData, error: stripeError } = await supabase.functions.invoke(
        "create-stripe-product",
        {
          body: {
            name: selectedProduct.name,
            description: selectedProduct.description,
            price: selectedProduct.price,
            imageUrl: selectedProduct.image_url,
            stripeProductId: selectedProduct.stripe_product_id,
          },
        }
      );

      if (stripeError) throw stripeError;

      // Update the database with the new Stripe IDs
      const { error } = await supabase
        .from("products")
        .update({
          stripe_price_id: stripeData.priceId,
          stripe_product_id: stripeData.productId,
        })
        .eq("id", selectedProduct.id);

      if (error) throw error;

      // Update local state
      setSelectedProduct({
        ...selectedProduct,
        stripe_price_id: stripeData.priceId,
        stripe_product_id: stripeData.productId,
      });

      toast({ title: "Product gesynchroniseerd met Stripe!" });
      fetchProducts();
    } catch (error: unknown) {
      toast({
        title: "Fout bij synchronisatie",
        description: getUserFriendlyError(error, "Stripe synchronisatie"),
        variant: "destructive",
      });
    }
  };

  const deleteProduct = async (id: string) => {
    if (!confirm("Weet je zeker dat je dit product wilt verwijderen?")) return;

    try {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;

      toast({ title: "Product verwijderd" });
      setSelectedProduct(null);
      fetchProducts();
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Product verwijderen"),
        variant: "destructive",
      });
    }
  };

  const syncAllProducts = async () => {
    try {
      toast({ title: "Alle producten synchroniseren met Stripe..." });
      
      const { data, error } = await supabase.functions.invoke("sync-all-products");
      
      if (error) throw error;
      
      await fetchProducts();
      
      toast({ 
        title: `Synchronisatie voltooid!`,
        description: `${data.synced} van ${data.total} producten gesynchroniseerd met Stripe.`
      });
    } catch (error: unknown) {
      toast({
        title: "Fout bij synchronisatie",
        description: getUserFriendlyError(error, "Alle producten synchroniseren"),
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({ title: "Uitgelogd" });
    navigate("/");
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-6 py-32">
          <p className="text-center">Laden...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Product detail view
  if (selectedProduct) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6">
          <div className="container mx-auto max-w-4xl">
            <Button
              variant="ghost"
              onClick={() => setSelectedProduct(null)}
              className="mb-4 sm:mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Terug naar overzicht
            </Button>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl sm:text-3xl">Product Bewerken</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Product Afbeelding</Label>
                  <div className="mt-2 space-y-4">
                    {selectedProduct.image_url && (
                      <img
                        src={selectedProduct.image_url}
                        alt={selectedProduct.name}
                        className="w-full max-w-md h-64 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, true)}
                        disabled={uploading}
                        className="max-w-md"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        {uploading ? "Uploaden..." : "Upload een nieuwe afbeelding"}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Productnaam</Label>
                    <Input
                      id="name"
                      value={selectedProduct.name}
                      onChange={(e) =>
                        setSelectedProduct({ ...selectedProduct, name: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="price">Prijs (€)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={selectedProduct.price}
                      onChange={(e) =>
                        setSelectedProduct({ ...selectedProduct, price: parseFloat(e.target.value) })
                      }
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Beschrijving</Label>
                    <Textarea
                      id="description"
                      value={selectedProduct.description || ""}
                      onChange={(e) =>
                        setSelectedProduct({ ...selectedProduct, description: e.target.value })
                      }
                      rows={6}
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="active"
                      checked={selectedProduct.active}
                      onCheckedChange={(checked) =>
                        setSelectedProduct({ ...selectedProduct, active: checked })
                      }
                    />
                    <Label htmlFor="active">Product is actief</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-on-homepage"
                      checked={selectedProduct.show_on_homepage}
                      onCheckedChange={(checked) =>
                        setSelectedProduct({ ...selectedProduct, show_on_homepage: checked })
                      }
                    />
                    <Label htmlFor="show-on-homepage">Toon op homepage</Label>
                  </div>

                  {selectedProduct.stripe_price_id && (
                    <div>
                      <Label>Stripe Price ID</Label>
                      <Input
                        value={selectedProduct.stripe_price_id}
                        disabled
                        className="mt-1 bg-muted"
                      />
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    <Button onClick={updateProduct} className="flex-1">
                      Wijzigingen Opslaan
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => deleteProduct(selectedProduct.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Verwijderen
                    </Button>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={syncProductWithStripe}
                    className="w-full"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Synchroniseer Prijs met Stripe
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // New product form
  if (showNewProductForm) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6">
          <div className="container mx-auto max-w-4xl">
            <Button
              variant="ghost"
              onClick={() => setShowNewProductForm(false)}
              className="mb-4 sm:mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Terug naar overzicht
            </Button>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl sm:text-3xl">Nieuw Product Toevoegen</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createProduct} className="space-y-6">
                  <div>
                    <Label>Product Afbeelding</Label>
                    <div className="mt-2 space-y-4">
                      {newProduct.image_url && (
                        <img
                          src={newProduct.image_url}
                          alt="Preview"
                          className="w-full max-w-md h-64 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, false)}
                          disabled={uploading}
                          className="max-w-md"
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          {uploading ? "Uploaden..." : "Upload een afbeelding"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="new-name">Productnaam</Label>
                      <Input
                        id="new-name"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new-price">Prijs (€)</Label>
                      <Input
                        id="new-price"
                        type="number"
                        step="0.01"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new-description">Beschrijving</Label>
                      <Textarea
                        id="new-description"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                        rows={6}
                        className="mt-1"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="new-active"
                        checked={newProduct.active}
                        onCheckedChange={(checked) => setNewProduct({ ...newProduct, active: checked })}
                      />
                      <Label htmlFor="new-active">Product is actief</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="new-show-homepage"
                        checked={newProduct.show_on_homepage}
                        onCheckedChange={(checked) => setNewProduct({ ...newProduct, show_on_homepage: checked })}
                      />
                      <Label htmlFor="new-show-homepage">Toon op homepage</Label>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={uploading}>
                    <Plus className="mr-2 h-4 w-4" />
                    Product Toevoegen
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Products list view
  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6 sm:mb-8">
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
              PRODUCTEN BEHEER
            </h1>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button onClick={() => setShowNewProductForm(true)} size="lg" className="w-full sm:w-auto">
                <Plus className="mr-2 h-5 w-5" />
                Nieuw Product
              </Button>
              <Button 
                onClick={syncAllProducts}
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
              >
                Sync naar Stripe
              </Button>
              <Button onClick={handleLogout} size="lg" variant="outline" className="w-full sm:w-auto">
                <LogOut className="mr-2 h-5 w-5" />
                Uitloggen
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow relative"
                onClick={() => setSelectedProduct(product)}
              >
                {/* Homepage badge linkerbovenhoek */}
                {product.show_on_homepage && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded shadow-md">
                      Homepage
                    </span>
                  </div>
                )}
                
                <div className="aspect-video w-full overflow-hidden bg-muted relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-display text-base sm:text-lg font-bold line-clamp-1 flex-1">
                      {product.name}
                    </h3>
                    {!product.active && (
                      <span className="text-xs bg-muted px-2 py-1 rounded ml-2">
                        Inactief
                      </span>
                    )}
                  </div>
                  <p className="text-foreground/60 text-sm line-clamp-2 mb-3">
                    {product.description}
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <p className="font-bold text-lg sm:text-xl">€{product.price.toFixed(2)}</p>
                    <Button size="sm" variant="outline" className="w-full sm:w-auto">
                      <Edit className="h-4 w-4 mr-2" />
                      Bewerken
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {products.length === 0 && (
            <Card className="p-12 text-center">
              <p className="text-foreground/60 mb-4">Nog geen producten toegevoegd</p>
              <Button onClick={() => setShowNewProductForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Eerste Product Toevoegen
              </Button>
            </Card>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AdminProducts;
